import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Check,
  PersonAdd,
  RestartAlt,
  Search,
  UploadFile,
} from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  Chip,
  Grid,
  Tab,
  Tabs,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useSessionStorage } from "@react-hooks-library/core";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { apiReportClient } from "@/api";
import { LinkCell } from "@/common/DataGridCells";
import { AuthStateContext } from "@/contexts/AuthContext";
import { IOrgUser } from "@/types";
import "./UserManagement.scss";
import {
  getAppDef,
  getParentDef,
} from "@/components/UserManagement/permissionTree";
import { Permission, UsersListFilter } from "@/components/UserManagement/types";
import {
  parseUserPermissions,
  userHasPermission,
  userPermissionChecks,
} from "@/components/UserManagement/utils";

import UploadUsersCSVDialog from "./UploadUsersCSVDialog";
import AccessOverviewSummary from "./UserPage/AccessOverviewSummary";
import ApplicationAccessCards from "./UserPage/ApplicationAccessCards";
import OrganizationAccessCards from "./UserPage/OrganizationAccessCards";

const useDynamicColumnWidths = (topPageRef) => {
  const calculateColumnWidths = useCallback((parentWidth) => {
    const baseWidth = parentWidth ? parentWidth * 0.6 : window.innerWidth * 0.6;

    const ratios = {
      first_name: 1,
      last_name: 1.5,
      email: 2,
      badge_number: 1,
      rank_short: 1,
    };
    const totalRatios = Object.values(ratios).reduce(
      (acc, val) => acc + val,
      0,
    );

    const columnWidths = {};
    for (const [key, value] of Object.entries(ratios)) {
      columnWidths[key] = (baseWidth / totalRatios) * value;
    }

    return columnWidths;
  }, []);

  const [columnWidths, setColumnWidths] = useState<{
    [key: string]: number;
  }>(() =>
    calculateColumnWidths(topPageRef.current?.parentElement?.offsetWidth),
  );
  useEffect(() => {
    const handleResize = () => {
      const parentWidth = topPageRef.current?.parentElement?.offsetWidth;
      setColumnWidths(calculateColumnWidths(parentWidth));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [topPageRef]);

  return columnWidths;
};

const isAdmin = (user: IOrgUser) =>
  parseUserPermissions(user.permissions ?? []).canManageAdmins === true;

const hasNoAppAccess = (user: IOrgUser) =>
  !user.permissions?.some((permission) => permission.title.startsWith("app."));

const matchesFilter = (user: IOrgUser, filter: UsersListFilter) => {
  switch (filter.kind) {
    case "admins":
      return isAdmin(user);
    case "noAppAccess":
      return hasNoAppAccess(user);
    case "permission":
      return userHasPermission(user, filter.permission);
  }
};

const UsersList = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { currentUser } = useContext(AuthStateContext);
  const { t } = useTranslation("common");

  const orgId = params.orgId;

  const [users, setUsers] = useState<IOrgUser[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const [activeFilter, setActiveFilter] = useState<UsersListFilter | null>(
    null,
  );

  const currentUserPermissions =
    currentUser?.permissions?.map((permission) => permission.title) || [];

  const permissionsChecks = userPermissionChecks(currentUserPermissions);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await apiReportClient.getUsersList(orgId);
      const sortedUsers = [...(response?.users || [])].sort((a, b) => {
        const lastNameA = (a.last_name || "").toLowerCase();
        const lastNameB = (b.last_name || "").toLowerCase();
        return lastNameA.localeCompare(lastNameB);
      });
      setUsers(sortedUsers);
    } catch (error) {
      navigate("/");
    }
  }, [orgId, navigate]);

  useEffect(() => {
    console.info("Fetching users list");
    fetchUsers();
  }, [fetchUsers]);

  const userClickHandler = (user) => {
    navigate(`/organizations/${orgId}/users/${user.id}`, {
      replace: true,
      state: { orgId },
    });
  };

  const renderLinkCell = (params) => {
    const id = params.id;
    return (
      <LinkCell
        value={params.value}
        onClick={() =>
          userClickHandler(filteredUsers.find((item) => item.id === id))
        }
      />
    );
  };

  const createNewUserHandler = () => {
    navigate(`/organizations/${orgId}/users/new`, {
      replace: true,
      state: { orgId },
    });
  };

  const topPageRef = useRef<HTMLDivElement | null>(null);

  const columnWidths = useDynamicColumnWidths(topPageRef);

  const hasLoginPermission = (user: IOrgUser) =>
    user.permissions?.some((p) => p.title === Permission.LOGIN) ?? false;

  const activeUsers = useMemo(() => users.filter(hasLoginPermission), [users]);
  const deactivatedUsers = useMemo(
    () => users.filter((u) => !hasLoginPermission(u)),
    [users],
  );

  const currentTabUsers = activeTab === 0 ? activeUsers : deactivatedUsers;

  const usersToSearch = useMemo(
    () =>
      activeFilter
        ? currentTabUsers.filter((user) => matchesFilter(user, activeFilter))
        : currentTabUsers,
    [currentTabUsers, activeFilter],
  );
  // Filter users based on search text
  const filteredUsers = useMemo(() => {
    if (!searchText.trim()) {
      return usersToSearch;
    }

    const searchLower = searchText.toLowerCase().trim();
    return usersToSearch.filter((user) => {
      const firstName = (user.first_name || "").toLowerCase();
      const lastName = (user.last_name || "").toLowerCase();
      const email = (user.email || "").toLowerCase();
      const badgeNumber = (user.badge_number || "").toLowerCase();
      const rank = (user.rank_short || "").toLowerCase();

      return (
        firstName.includes(searchLower) ||
        lastName.includes(searchLower) ||
        email.includes(searchLower) ||
        badgeNumber.includes(searchLower) ||
        rank.includes(searchLower) ||
        `${firstName} ${lastName}`.includes(searchLower)
      );
    });
  }, [usersToSearch, searchText]);

  const columns: GridColDef<IOrgUser>[] = [
    {
      field: "first_name",
      headerName: "First Name",
      width: columnWidths.first_name,
      renderCell: renderLinkCell,
    },
    {
      field: "last_name",
      headerName: "Last Name",
      width: columnWidths.last_name,
      renderCell: renderLinkCell,
    },
    {
      field: "email",
      headerName: "Email",
      width: columnWidths.email,
    },

    {
      field: "badge_number",
      headerName: "Badge",
      width: columnWidths.badge_number,
      renderCell: renderLinkCell,
    },
    {
      field: "rank_short",
      headerName: "Rank",
      width: columnWidths.rank_short,
    },
    {
      field: "is_admin",
      headerName: t("userManagement.usersList.admin"),
      type: "boolean",
      width: 120,
      valueGetter: (_value, row) => isAdmin(row),
      renderCell: (params) =>
        params.value ? (
          <Check
            fontSize="small"
            titleAccess={t("userManagement.usersList.admin")}
          />
        ) : null,
    },
  ];

  const [paginationModel, setPaginationModel] = useSessionStorage(
    "userListPaginationModel",
    { page: 0, pageSize: 25 },
  );

  const resetToFirstPage = () => setPaginationModel({ page: 0, pageSize: 25 });

  const selectFilter = (filter: UsersListFilter | null) => {
    setActiveFilter(filter);
    resetToFirstPage();
  };

  const resetView = () => {
    setActiveFilter(null);
    setSearchText("");
    resetToFirstPage();
  };

  const filterLabel = (filter: UsersListFilter) => {
    if (filter.kind === "admins") {
      return t("userManagement.accessOverview.administrators");
    }
    if (filter.kind === "noAppAccess") {
      return t("userManagement.accessOverview.withoutAppAccess");
    }
    const app = getAppDef(filter.permission);
    if (app) {
      return t(app.shortLabelKey ?? app.labelKey);
    }
    const parent = getParentDef(filter.permission);
    const sub = parent?.subPermissions?.find(
      (s) => s.permission === filter.permission,
    );
    if (!parent || !sub) {
      return filter.permission;
    }
    return `${t(parent.shortLabelKey ?? parent.labelKey)} › ${t(sub.labelKey)}`;
  };

  const CreateUserButton = () => {
    return (
      <Button
        variant="contained"
        color="primary"
        onClick={() => createNewUserHandler()}
        startIcon={<PersonAdd />}
        className="create-user-button"
      >
        Create User
      </Button>
    );
  };

  const UploadCSVButton = () => {
    return (
      <Button
        variant="outlined"
        color="primary"
        onClick={() => setUploadDialogOpen(true)}
        startIcon={<UploadFile />}
      >
        Upload CSV
      </Button>
    );
  };

  const adminCount = currentTabUsers.filter(isAdmin);
  const noAppAccess = currentTabUsers.filter(hasNoAppAccess);

  return (
    <div className="users_list_container">
      <div id="top-page-div" ref={topPageRef}></div>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
            }}
          >
            {permissionsChecks.canCreateUsers && <UploadCSVButton />}
            {permissionsChecks.canCreateUsers && <CreateUserButton />}
          </Box>
        </Grid>
      </Grid>
      <Tabs
        value={activeTab}
        onChange={(_e, newValue) => {
          setActiveTab(newValue);
          resetToFirstPage();
        }}
        sx={{ mb: 2 }}
      >
        <Tab label={`Active Users (${activeUsers.length})`} />
        <Tab label={`Deactivated Users (${deactivatedUsers.length})`} />
      </Tabs>
      <div className="accordion">
        <Accordion sx={{ width: "100%" }}>
          <AccordionSummary
            sx={{
              display: "flex",
              width: "100%",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
            expandIcon={<ExpandMoreIcon />}
          >
            <Typography sx={{ fontWeight: "bold", pr: "25px" }}>
              {t("userManagement.accessOverview.title")}
            </Typography>
            <AccessOverviewSummary
              currentTabUsers={currentTabUsers}
              adminCount={adminCount}
              noAppAccess={noAppAccess}
            />
          </AccordionSummary>
          <AccordionDetails>
            <OrganizationAccessCards
              currentTabUsers={currentTabUsers}
              adminCount={adminCount}
              noAppAccess={noAppAccess}
              activeFilter={activeFilter}
              onSelectFilter={selectFilter}
            />
            <Typography
              sx={{
                pt: "20px",
                pb: "8px",
                textTransform: "uppercase",
                color: "text.secondary",
                fontSize: "0.75rem",
                fontWeight: "bold",
              }}
            >
              {t("userManagement.accessOverview.applicationAccess")}
            </Typography>
            <ApplicationAccessCards
              orgId={orgId ?? "NA"}
              currentTabUsers={currentTabUsers}
              activeFilter={activeFilter}
              onSelectFilter={selectFilter}
            />
          </AccordionDetails>
        </Accordion>
      </div>
      <div className="user-tools">
        <Grid container spacing={2} className="user-search-container">
          {(activeFilter || searchText) && (
            <Grid
              size="auto"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              {activeFilter && (
                <Chip
                  label={t("userManagement.accessOverview.activeFilter", {
                    filter: filterLabel(activeFilter),
                  })}
                  color="primary"
                  variant="outlined"
                />
              )}
              <Button
                variant="outlined"
                startIcon={<RestartAlt />}
                onClick={resetView}
              >
                {t("userManagement.accessOverview.resetView")}
              </Button>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search users by name, email, badge, or rank..."
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                resetToFirstPage();
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              inputProps={{
                "aria-label": "Search users",
              }}
            />
          </Grid>
        </Grid>
      </div>
      <DataGrid
        rows={filteredUsers?.length > 0 ? filteredUsers : []}
        columns={columns}
        sx={{
          "& .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight)>.MuiDataGrid-cell":
            {
              whiteSpace: "normal",
              wordWrap: "break-word",
            },
        }}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 25, 50, 100]}
        onRowClick={(params) => {
          const user = filteredUsers.find((item) => item.id === params.id);
          if (user) {
            userClickHandler(user);
          }
        }}
      />
      <UploadUsersCSVDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        orgId={orgId ?? ""}
        onUploadSuccess={fetchUsers}
        existingUsers={users}
      />
    </div>
  );
};

export default UsersList;
